import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Drawer,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { Alert, AlertTitle } from '@material-ui/lab';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewDispatch,
  useDynamicViewState,
} from '../../context/dynamic-view-context';
import { useDynamicPanelsUser } from '../../hooks/useDynamicPanelsUser';
// import { DialogNewPanel } from './DialogNewPanel';
// import { PlantillaNewPanel } from './PlantillaNewPanel';
import { TemplateCardSelect } from './TemplateCardSelect';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { toggleHomeDynamicPanel } from '../../../../services/dynamicPanels';

/**
 * @fileoverview Sección de gestión de paneles/dashboards del usuario.
 * @module PanelsSection
 */

const DialogNewPanel = React.lazy(() => import('./DialogNewPanel'));
const PlantillaNewPanel = React.lazy(() => import('./PlantillaNewPanel'));

/**
 * Componente PanelsSection.
 *
 * Muestra un botón que abre un cajón lateral (Drawer) con la lista de todos los
 * dashboards creados por el usuario. Desde aquí, el usuario puede:
 * - Ver sus dashboards existentes.
 * - Seleccionar un dashboard para cargarlo.
 * - Eliminar dashboards existentes.
 * - Crear nuevos dashboards (a través de `DialogNewPanel`).
 *
 * @returns {React.ReactElement} Botón y Drawer de gestión de paneles.
 * 
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function PanelsSection({ isPanelsOpen, handleClosePanels }) {
  const { t } = useTranslation();
  const { data: panels, error, refetch } = useDynamicPanelsUser({
    enabled: true,
    favourites: false,
  });
  // const [isPanelsOpen, setIsPanelsOpen] = React.useState(false);
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  const queryClient = useQueryClient();

  /**
   * Carga un dashboard seleccionado en el área principal.
   *
   * Actualiza el estado global con:
   * - La estructura del layout.
   * - El objetivo asociado.
   * - El ID del panel activo.
   *
   * @param {Object} layout - Objeto con la configuración del dashboard seleccionado.
   */
  function updateSlotParam(layout) {
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT,
      payload: layout.estructura_panel_dinamico,
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_GLOBAL_OBJECTIVE,
      payload: {
        objetivoId: layout.objetivo_id,
        applyToAll: layout.apply_to_all_objetivo_id,
      },
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_ACTIVE_LAYOUT_ID,
      payload: layout.panel_dinamico_usuario_id,
    });
  }

  /**
   * Callback ejecutado tras la eliminación exitosa de un dashboard.
   *
   * Refresca la lista de paneles y fuerza la invalidación de la caché de react-query
   * para asegurar que la UI muestre datos actualizados.
   */
  function onDeleteSuccess() {
    refetch();
    queryClient.invalidateQueries({
      queryKey: ['dynamicPanelsUser'],
    });
    queryClient.invalidateQueries({
      queryKey: ['dynamicPanelHomeUser'],
    });
  }

  const handleHome = async (panelId, isHome) => {
    const queryKey = ['dynamicPanelsUser', { favourites: false }];
  
    const previousPanels = queryClient.getQueryData(queryKey);

    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return oldData;
      return oldData.map(p => ({
        ...p,
        mostrar_en_home: isHome 
          ? p.panel_dinamico_usuario_id === panelId
          : p.panel_dinamico_usuario_id === panelId ? false : p.mostrar_en_home 
      }));
    });

    try {
      if (isHome) {
        const currentHome = previousPanels?.find(p => p.mostrar_en_home === true);
        if (currentHome && currentHome.panel_dinamico_usuario_id !== panelId) {
          await toggleHomeDynamicPanel({
            panelId: currentHome.panel_dinamico_usuario_id,
            enHome: false
          });
        }
      }

      const response = await toggleHomeDynamicPanel({
        panelId: panelId,
        enHome: isHome
      });

      if (!response) throw new Error("API Error");

      toast.success(
        isHome
          ? t('dynamicPanel.templateCardSelect.homeAdded')
          : t('dynamicPanel.templateCardSelect.homeRemoved')
      );
      
      return true;

    } catch (error) {
      console.error("Error al actualizar Home, revirtiendo estado:", error);
      queryClient.setQueryData(queryKey, previousPanels);
      
      toast.error(t('dynamicPanel.templateCardSelect.updateHomeError'));
      return false;
    }
  };

  return (
    <React.Fragment>
      <Drawer
        anchor="right"
        open={isPanelsOpen}
        onClose={handleClosePanels}
        ModalProps={{ keepMounted: true, disableScrollLock: true }}
      >

        <Box width={{ xs: '100vw', sm: 500 }} role="presentation">
          <Box display="flex" justifyContent="flex-end" p={1}>
            <IconButton onClick={handleClosePanels}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Card elevation={0}>
            <CardHeader
              title={
                <Box>
                  <Typography variant="h4">
                    {t('dynamicPanel.panelsSection.myDashboards')}
                  </Typography>
                  <Typography variant="subtitle1">
                    {t('dynamicPanel.panelsSection.description')}
                  </Typography>
                </Box>
              }
            />
            <CardContent>
              {error && (
                <Typography color="error">
                  {t('dynamicPanel.panelsSection.loadError')}
                </Typography>
              )}
              {panels && !panels.length && (
                <Alert severity="info">
                  <AlertTitle>
                    {t('dynamicPanel.panelsSection.noPanelsTitle')}
                  </AlertTitle>
                  <Typography>
                    {t('dynamicPanel.panelsSection.noPanelsText')}
                  </Typography>
                </Alert>
              )}
              {panels && panels.length > 0 && (
                <Box>
                  <Grid container spacing={2}>
                    {panels.map((panel) => (
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        key={panel.panel_dinamico_usuario_id}
                      >
                        <TemplateCardSelect
                          panel={panel}
                          onDeleteSuccess={onDeleteSuccess}
                          handleHome={handleHome}
                        />
                        <Suspense fallback={<CircularProgress size={24} />}>
                          <PlantillaNewPanel
                            key={panel.panel_dinamico_usuario_id}
                            estructura={panel.estructura_panel_dinamico}
                            isSelected={
                              state.activeLayoutId ===
                              panel.panel_dinamico_usuario_id
                            }
                            onSelect={() => updateSlotParam(panel)}
                          />
                        </Suspense>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              <Suspense fallback={<CircularProgress size={24} />}>
                <DialogNewPanel refetchPanels={refetch} />
              </Suspense>
            </CardContent>
          </Card>
        </Box>
      </Drawer>
    </React.Fragment>
  );
}
